"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var UUID_1 = require("./UUID");
var Attributes_1 = require("./Attributes");
var ComponentStore_1 = require("./ComponentStore");
var Deprecated_1 = require("./Deprecated");
var assign = require("object-assign");
/**
 * The base for all Armature components.
 */
var Component = (function () {
    /**
     * Creates a new component with the given data.
     *
     * @param data The data to initialize the component with
     */
    function Component(state) {
        /**
         * The components this component contains.
         */
        this.children = [];
        /**
         * A flag telling ekma that this object yields HTML
         */
        this.safeInHTML = true;
        this.$onChange = [];
        if (state == null) {
            state = this.getDefaultState();
            if (state == null) {
                throw new Error(("Component \"" + this.constructor.name + "\" was created with no data specified!\n") +
                    "Additionally, the component's $getDefaultState method returned null.\n" +
                    "If this was intentional, override $getDefaultState to return a non-null object to use.");
            }
        }
        this.id = UUID_1.default.get();
        this.state = state;
    }
    /**
     * Deprecated: use Parent#getChild and Child#setParent instead.
     *
     * Creates or retrieves a component that is attached to another component.
     * Using the same parent and 'label' parameters will retrieve the same object.
     * The third parameter is passed directly to the constructor of a created component.
     *
     * @param parent The component to attach to
     * @param label The label to use when creating or retrieving the component
     * @param state The state to construct the component with if it hasn't been created.
     */
    Component.for = function (parent, label, state) {
        var identifier = this.getIdentifier(label);
        var existing = parent.children.find(function (v) { return v.getIdentifier() === identifier; });
        if (existing) {
            return existing;
        }
        var inst = new this(state);
        inst.label = label;
        inst.parent = parent;
        parent.children.push(inst);
        return inst;
    };
    /**
     * Returns the child component of the given type and label, if it exists.
     * Returns a Component<any>, requires casting to the type passed to the function.
     *
     * @param childClass The child's type to search for.
     * @param label The child's label to search for.
     */
    Component.prototype.getChild = function (childClass, label) {
        var id = childClass.getIdentifier(label);
        return this.children.find(function (v) { return v.getIdentifier() === id; });
    };
    /**
     * Sets the label of the component and returns it.
     *
     * @param label The label to set.
     */
    Component.prototype.setLabel = function (label) {
        this.label = label;
        return this;
    };
    /**
     * Sets this component to be a child of the given component.
     * A component can only be the child of one component.
     * This method unsets an existing parent if one was set.
     *
     * @param parent The component to contain this one.
     */
    Component.prototype.setParent = function (parent) {
        var _this = this;
        if (this.parent) {
            this.parent.children = this.parent.children.filter(function (v) { return v !== _this; });
        }
        this.parent = parent;
        parent.children.push(this);
        return this;
    };
    /**
     * Returns the default state of the component if none is given at construction time.
     * Returning null from this function signifies that state must be specified.
     */
    Component.prototype.getDefaultState = function () {
        return null;
    };
    Component.prototype.setState = function (state) {
        assign(this.state, state);
        this.$onChange.forEach(function (f) { return f(); });
    };
    Component.prototype.onChange = function (method) {
        this.$onChange.push(method);
    };
    /**
     * Serializes the component's data for potential recreation.
     */
    Component.prototype.packState = function () {
        return assign({}, this.state);
    };
    Component.unpackState = function (state) {
        return state;
    };
    /**
     * Returns a way to uniquely identify this component type.
     */
    Component.getTypeName = function () {
        var classString = this.htmlClassNames.join("+");
        return this.htmlTagName + ";" + classString;
    };
    /**
     * Creates an object that can be used to recreate this component.
     */
    Component.prototype.deflate = function () {
        var thisClass = this.constructor;
        return {
            type: thisClass.getTypeName(),
            state: this.packState(),
            id: this.id,
            label: this.label,
            children: this.children.map(function (v) { return v.deflate(); })
        };
    };
    /**
     * Creates an instance of a component from state generated by $deflate.
     *
     * @param deflated The deflated component state to use
     * @param inst An optional existing object to inflate
     */
    Component.inflate = function (deflated, inst) {
        var thisClass = ComponentStore_1.default.get(deflated.type);
        if (inst == null) {
            inst = new thisClass(this.unpackState(deflated.state));
        }
        inst.id = deflated.id;
        inst.label = deflated.label;
        if (deflated.children) {
            for (var _i = 0, _a = deflated.children; _i < _a.length; _i++) {
                var child = _a[_i];
                var thatClass = ComponentStore_1.default.get(child.type);
                var childInst = thatClass.for(inst, child.label, thatClass.unpackState(child.state));
                thatClass.inflate(child, childInst);
            }
        }
        return inst;
    };
    /**
     * Returns an identifier that should be unique among a component's siblings.
     *
     * @param label The label of the component to create the label for
     */
    Component.getIdentifier = function (label) {
        return this.getTypeName() + "__" + label;
    };
    /**
     * Returns an identifier that should be unique among a component's siblings.
     */
    Component.prototype.getIdentifier = function () {
        var thisClass = this.constructor;
        return thisClass.getIdentifier(this.label);
    };
    Component.prototype.toString = function () {
        return this.getHTML();
    };
    /**
     * Returns the complete HTML used to render this component.
     */
    Component.prototype.getHTML = function () {
        var thisClass = this.constructor;
        var attributes = [
            ("class=\"" + thisClass.htmlClassNames.join(" ") + "\""),
            ("data-arm-id=\"" + this.id + "\"")
        ];
        if (this.label) {
            attributes.push("data-label=\"" + this.label + "\"");
        }
        return "<" + thisClass.htmlTagName + " " + attributes.join(" ") + ">" + this.template(this) + "</" + thisClass.htmlTagName + ">";
    };
    /**
     * Creates an HTML element for this component if one does not exist.
     */
    Component.prototype.ensureElement = function () {
        if (this.element == null) {
            var thisClass = this.constructor;
            var el = document.createElement(thisClass.htmlTagName);
            (_a = el.classList).add.apply(_a, thisClass.htmlClassNames);
            this.attachTo(el);
        }
        var _a;
    };
    /**
     * Attaches this component to an existing HTML element.
     *
     * @param element The element to attach to
     */
    Component.prototype.attachTo = function (element) {
        this.element = element;
        element.setAttribute("data-arm-id", this.id.toString());
        if (this.label) {
            element.setAttribute("data-label", this.label);
        }
    };
    /**
     * Attempts to locate this component's element from a parent element.
     *
     * @param parent The element to search in
     */
    Component.prototype.locate = function (parent) {
        if (parent === void 0) { parent = document.body; }
        var thisClass = this.constructor;
        var el = parent.querySelector(thisClass.htmlTagName + "[data-arm-id=\"" + this.id + "\"]");
        if (el && el !== this.element) {
            this.element = el;
            for (var _i = 0, _a = this.children; _i < _a.length; _i++) {
                var child = _a[_i];
                child.locate(el);
            }
        }
    };
    /**
     * Evaluates the component's template and sets the component's element's HTML to the result.
     */
    Component.prototype.render = function () {
        if (this.element == null) {
            throw new Error(("Component \"" + this.constructor.name + "\" was rendered before it had an HTML element attached to it!\n") +
                "When running on the server, use $getHTML() or toString() instead of calling render().\n" +
                "On the client, use reify(). Use attachTo() beforehand to render into an existing HTML element.");
        }
        this.element.removeAttribute("data-arm-installed");
        this.element.innerHTML = this.template(this).toString();
    };
    Component.prototype.shouldInstall = function () {
        return this.element.getAttribute("data-arm-installed") == null;
    };
    /**
     * Attaches event handlers for the component and its children.
     * Often used after render, or as part of reify.
     */
    Component.prototype.install = function () {
        for (var _i = 0, _a = this.children; _i < _a.length; _i++) {
            var child = _a[_i];
            if (child.element == null || !this.element.contains(child.element)) {
                child.locate(this.element);
            }
            if (child.element != null) {
                child.install();
            }
        }
        if (this.shouldInstall()) {
            this.element.setAttribute("data-arm-installed", "");
            this.installed();
        }
    };
    Component.prototype.installed = function () {
    };
    Component.prototype.shouldUninstall = function () {
        return this.element.getAttribute("data-arm-installed") != null;
    };
    /**
     * Detaches event handlers for the component and its children.
     */
    Component.prototype.uninstall = function () {
        for (var _i = 0, _a = this.children; _i < _a.length; _i++) {
            var child = _a[_i];
            child.uninstall();
        }
        if (this.shouldUninstall()) {
            this.element.removeAttribute("data-arm-installed");
            this.uninstalled();
        }
    };
    Component.prototype.uninstalled = function () {
    };
    /**
     * Ensures this component's element exists, then renders and installs it.
     */
    Component.prototype.reify = function () {
        this.ensureElement();
        this.render();
        this.install();
    };
    /**
     * A list of class names to apply to the component's root element.
     */
    Component.htmlClassNames = [];
    __decorate([
        Deprecated_1.DeprecatedMethod("Armature: Component.for was deprecated in favor of Parent.getChild and Child.setParent in 1.0.0-alpha4")
    ], Component, "for", null);
    Component = __decorate([
        Attributes_1.default({
            tag: "arm-template",
            template: function () { return ""; }
        })
    ], Component);
    return Component;
}());
exports.Component = Component;

//# sourceMappingURL=Component.js.map
